import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ArrowRight, ShoppingCart, X, TrendingDown } from 'lucide-react';
import { useCompare } from '@/lib/compareContext';
import { useCart } from '@/lib/cartContext';
import { estimateMonthlySavings, formatINR, formatINRCompact } from '@/lib/savings';
import { MiniCartDrawer } from '@/components/MiniCartDrawer';
import { useState } from 'react';

const SPEC_ROWS = [
  { label: 'Power (kW)', key: 'pcs_kw', format: v => `${v} kW` },
  { label: 'Energy (kWh)', key: 'battery_kwh', format: v => `${v} kWh` },
  { label: 'Form Factor', key: 'form_factor' },
  { label: 'Cooling', key: 'cooling' },
  { label: 'Type', key: 'sku_type' },
  { label: 'Buy Price (from)', key: 'buy_price', dynamic: true },
  { label: 'Rent Price (from)', key: 'rent_price', dynamic: true },
  { label: 'Est. Savings/mo', key: 'savings', dynamic: true },
];

export default function ComparePage() {
  const { compareItems, removeFromCompare, clearCompare } = useCompare();
  const { addItem } = useCart();
  const [miniCartOpen, setMiniCartOpen] = useState(false);
  const [lastAdded, setLastAdded] = useState(null);

  const handleAddToCart = (product, mode) => {
    addItem(product, mode);
    setLastAdded({ ...product, mode });
    setMiniCartOpen(true);
  };

  if (compareItems.length === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="compare-empty">
        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-2xl font-bold text-[#1A1A1A] mb-2">No products to compare</h1>
          <p className="text-[#57534E] mb-6">Add up to 3 products from the catalogue to compare specifications side by side.</p>
          <Link to="/products">
            <Button className="rounded-full bg-[#0061DD] text-white px-8 py-5" data-testid="compare-browse-btn">
              Browse Products
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="compare-page">
      <section className="py-10 lg:py-14">
        <div className="voltide-container">
          <div className="flex items-center justify-between mb-8">
            <div>
              <Link to="/products" className="inline-flex items-center gap-2 text-sm text-[#57534E] hover:text-[#0061DD] mb-3" data-testid="compare-back-btn">
                <ArrowLeft className="w-4 h-4" /> Back to Products
              </Link>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A]">Compare Products</h1>
              <p className="text-sm text-[#57534E]">{compareItems.length} of 3 products selected</p>
            </div>
            <Button onClick={clearCompare} variant="outline" className="rounded-full text-sm" data-testid="compare-clear-btn">
              Clear All
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]" data-testid="compare-table">
              {/* Product Images + Names */}
              <thead>
                <tr>
                  <th className="w-40 p-3 text-left text-xs font-medium text-[#A8A29E] uppercase tracking-wider">Specification</th>
                  {compareItems.map(p => (
                    <th key={p.sku} className="p-3 text-center min-w-[200px]">
                      <div className="bento-card p-4 relative">
                        <button
                          onClick={() => removeFromCompare(p.sku)}
                          className="absolute top-2 right-2 p-1 rounded-full hover:bg-stone-100 text-[#A8A29E] hover:text-red-500"
                          data-testid={`compare-remove-${p.sku}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <img src={p.image} alt={p.product_name} className="w-24 h-24 object-contain mx-auto mb-3" />
                        <Link to={`/products/${p.sku}`} className="text-sm font-bold text-[#1A1A1A] hover:text-[#0061DD]">
                          {p.product_name}
                        </Link>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SPEC_ROWS.map((row, idx) => (
                  <tr key={row.key} className={idx % 2 === 0 ? 'bg-stone-50/50' : ''}>
                    <td className="p-3 text-sm font-medium text-[#57534E]">{row.label}</td>
                    {compareItems.map(p => {
                      let value;
                      if (row.key === 'buy_price') {
                        const bp = p.buy_from_price_ex_gst;
                        value = bp ? formatINR(bp) : <span className="text-xs text-[#0061DD]">Request quote</span>;
                      } else if (row.key === 'rent_price') {
                        value = p.rent_from_inr_per_kwh_month
                          ? `₹${p.rent_from_inr_per_kwh_month}/kWh/mo`
                          : <span className="text-xs text-[#0061DD]">Request quote</span>;
                      } else if (row.key === 'savings') {
                        const s = estimateMonthlySavings(p.battery_kwh);
                        value = (
                          <span className="flex items-center justify-center gap-1 text-emerald-700">
                            <TrendingDown className="w-3 h-3" />
                            {formatINRCompact(s.low)}–{formatINRCompact(s.high)}
                          </span>
                        );
                      } else {
                        const raw = p[row.key];
                        value = row.format ? row.format(raw) : (raw || '—');
                      }
                      return (
                        <td key={p.sku} className="p-3 text-sm text-center mono text-[#1A1A1A]" data-testid={`compare-${row.key}-${p.sku}`}>
                          {value}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {/* Add to Cart row */}
                <tr>
                  <td className="p-3 text-sm font-medium text-[#57534E]">Actions</td>
                  {compareItems.map(p => (
                    <td key={p.sku} className="p-3 text-center">
                      <div className="flex flex-col gap-2">
                        <Button
                          onClick={() => handleAddToCart(p, 'rent')}
                          className="rounded-full bg-[#0061DD] hover:bg-[#004BB3] text-white text-xs gap-1"
                          data-testid={`compare-add-rent-${p.sku}`}
                        >
                          <ShoppingCart className="w-3.5 h-3.5" /> Add (Rent)
                        </Button>
                        <Button
                          onClick={() => handleAddToCart(p, 'buy')}
                          variant="outline"
                          className="rounded-full border-stone-200 text-xs gap-1"
                          data-testid={`compare-add-buy-${p.sku}`}
                        >
                          <ShoppingCart className="w-3.5 h-3.5" /> Add (Buy)
                        </Button>
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Bottom CTA */}
          <div className="mt-8 text-center">
            <Link to="/quote">
              <Button className="rounded-full bg-[#0061DD] hover:bg-[#004BB3] text-white px-8 py-5 text-base font-semibold gap-2" data-testid="compare-quote-btn">
                Get a Quote <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MiniCartDrawer open={miniCartOpen} onClose={() => setMiniCartOpen(false)} lastItem={lastAdded} />
    </div>
  );
}
